import React from 'react';
import { Link } from 'react-router-dom';
import {
 ArrowRight,
 Percent,
 CreditCard,
 Smartphone,
 Bell,
 Users,
 Crown,
 ShieldCheck,
 Calculator,
 Sparkles,
 Zap,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';

/**
 * WhyNoFee — high-level overview hub.
 *
 * Sits at /for-restaurants/why-nofee. Premium, calm summary of the business case.
 * Links down to the two detailed product pages (Ordering / POS Lite).
 */

const PILLARS = [
 {
 icon: Percent,
 title: '0% commission, forever',
 body: 'Flat $299/month replaces per-order commissions. The numbers move from a marketplace’s pocket into yours.',
 },
 {
 icon: CreditCard,
 title: 'Direct Stripe payouts',
 body: 'Payments go directly to your own Stripe account, not a marketplace clearing account. NoFee never holds your money — you see every transaction.',
 },
 {
 icon: Smartphone,
 title: 'QR & online ordering',
 body: 'Your own branded ordering page and at-table QR menus. Apple Pay, Google Pay — all included.',
 },
 {
 icon: Bell,
 title: 'Real-time SMS & email',
 body: 'Order notifications to your team and your customer the moment an order is placed. Built on Twilio + Resend.',
 },
 {
 icon: Users,
 title: 'You own the relationship',
 body: 'Your customers, your data, your email list. No marketplace standing between you and the people who pay for your food.',
 },
 {
 icon: Zap,
 title: '⚡ Fast by NoFee — Customer-Verified On-Time Delivery',
 body: 'Restaurants earn trust and visibility through verified on-time deliveries confirmed by real customers. Fast Score is available inside your dashboard for operational monitoring — a real competitive advantage on the directory.',
 },
 {
 icon: Crown,
 title: 'Built for modern restaurants',
 body: 'Multi-language menus (EN/PT/ES), kitchen tickets, printable receipts, NoFee Rewards loyalty — and POS Lite coming next.',
 },
];

const WhyNoFee = () => {
 return (
 <LanguageProvider>
 <div className="min-h-screen bg-black text-white">
 <SEO
 title="Why Restaurants Choose NoFee — 0% Commission, Direct Payouts"
 description="Why US restaurants switch from DoorDash and Uber Eats to NoFee: 0% commission, direct Stripe payouts, branded ordering, and full ownership of the customer relationship."
 path="/for-restaurants/why-nofee"
 />
 <Navbar />

 {/* Hero */}
 <section className="relative pt-32 sm:pt-36 lg:pt-44 pb-14">
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.10),transparent_60%)]" />
 <div className="relative max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/30 bg-gold-500/[0.04] mb-6">
 <Sparkles className="w-3.5 h-3.5 text-gold-300" />
 <span className="text-[11px] uppercase tracking-[0.24em] text-gold-300 font-semibold">
 Why NoFee
 </span>
 </div>
 <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
 Built for restaurants
 <br />
 <span className="bg-gradient-to-r from-amber-200 via-gold-400 to-amber-300 bg-clip-text text-transparent">
 that keep what they earn.
 </span>
 </h1>
 <p className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-white/65 leading-relaxed">
 NoFee replaces commission-based marketplaces with a flat subscription.
 The savings are real, the payouts are direct, and your customer relationship is yours again.
 </p>
 <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
 <Link
 to="/savings-calculator"
 data-testid="why-cta-calculator"
 className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-extrabold transition-all hover:shadow-[0_15px_40px_-15px_rgba(212,175,55,0.55)]"
 >
 <Calculator className="w-4 h-4" />
 See your savings
 </Link>
 <Link
 to="/start?plan=ordering&utm_source=why_nofee"
 data-testid="why-cta-start"
 className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-white/15 hover:border-gold-500/50 text-white/85 hover:text-gold-200 font-bold transition-all"
 >
 Start free trial
 <ArrowRight className="w-4 h-4" />
 </Link>
 </div>
 </div>
 </section>

 {/* Six pillars */}
 <section className="relative pb-16 lg:pb-20" data-testid="why-pillars">
 <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
 {PILLARS.map((p) => (
 <div
 key={p.title}
 className="rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-6 sm:p-7 hover:border-gold-500/30 transition-all duration-300"
 data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
 >
 <div className="w-11 h-11 rounded-xl bg-gold-500/10 border border-gold-500/25 flex items-center justify-center mb-5">
 <p.icon className="w-5 h-5 text-gold-300" />
 </div>
 <h3 className="text-lg font-black text-white tracking-tight">
 {p.title}
 </h3>
 <p className="mt-2 text-[14px] text-white/65 leading-relaxed">
 {p.body}
 </p>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* Product split — detailed pages */}
 <section className="relative pb-16 lg:pb-20" data-testid="why-products">
 <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
 <div className="text-center mb-10">
 <p className="text-[11px] uppercase tracking-[0.22em] text-gold-400 font-bold">
 Two products · one ecosystem
 </p>
 <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
 Where do you want to start?
 </h2>
 </div>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 <ProductCard
 badge="Live now"
 tone="live"
 title="NoFee Ordering"
 description="Online & QR ordering, direct payouts, kitchen tickets. Replaces DoorDash and Uber Eats for your own customers."
 to="/for-restaurants/ordering"
 testId="product-ordering"
 />
 <ProductCard
 badge="Early access"
 tone="soon"
 title="NoFee POS Lite"
 description="iPad-first restaurant operating system: dine-in, counter, table management, employee logins."
 to="/for-restaurants/pos-lite"
 testId="product-pos"
 />
 </div>
 </div>
 </section>

 {/* Closing strip */}
 <section className="relative pb-24 lg:pb-32">
 <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
 <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 text-sm text-white/55">
 <div className="inline-flex items-center gap-2">
 <ShieldCheck className="w-4 h-4 text-gold-400" />
 <span>Secure payments by Stripe</span>
 </div>
 <div className="hidden sm:block w-px h-4 bg-white/10" />
 <div className="inline-flex items-center gap-2">
 <Percent className="w-4 h-4 text-gold-400" />
 <span>0% commission · forever</span>
 </div>
 <div className="hidden sm:block w-px h-4 bg-white/10" />
 <div className="inline-flex items-center gap-2">
 <Sparkles className="w-4 h-4 text-gold-400" />
 <span>US restaurant first</span>
 </div>
 </div>
 </div>
 </section>

 <FooterSection />
 </div>
 </LanguageProvider>
 );
};

const ProductCard = ({ badge, tone, title, description, to, testId }) => {
 const isLive = tone === 'live';
 return (
 <Link
 to={to}
 data-testid={testId}
 className={`group block rounded-3xl border p-7 sm:p-8 transition-all duration-300 hover:-translate-y-0.5 ${
 isLive
? 'border-gold-500/30 bg-gradient-to-b from-zinc-950 to-black hover:border-gold-500/55 hover:shadow-[0_30px_80px_-30px_rgba(212,175,55,0.3)]'
: 'border-amber-500/20 bg-gradient-to-b from-zinc-950 to-black hover:border-amber-500/40'
 }`}
 >
 <div className="flex items-center justify-between mb-5">
 <span
 className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-[0.16em] uppercase border ${
 isLive
? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
: 'bg-amber-500/10 border-amber-500/30 text-amber-300'
 }`}
 >
 {isLive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
 {badge}
 </span>
 <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-gold-300 group-hover:translate-x-0.5 transition-all" />
 </div>
 <h3 className="text-2xl font-black text-white tracking-tight">{title}</h3>
 <p className="mt-2 text-[14.5px] text-white/65 leading-relaxed">{description}</p>
 <p className="mt-5 text-[12px] text-gold-300/80 font-bold tracking-wide">
 Explore features →
 </p>
 </Link>
 );
};

export default WhyNoFee;
